// Metadatos específicos de Consola V3 (Central Galarya).
// Eje TIPO (qué pasó) y eje PRIORIDAD (qué tan urgente) son INDEPENDIENTES.
// Familia PERSONA vs TÉCNICO se distingue por estructura + iconografía + lenguaje + posición + label,
// NO sólo por color. El acento de color de una alarma de persona lo da su PRIORIDAD
// (ALTA = coral/rojo, MEDIA = ámbar). Las incidencias técnicas usan rojo ladrillo + franja rayada discreta.
import {
  PersonStanding, Activity, LogOut, Clock, AudioLines, Siren,
  WifiOff, ShieldOff, Router, ServerOff, Bell, HeartPulse, Wind, PhoneCall, BatteryWarning,
} from "lucide-react";
import { TIPOS_TECNICOS, elapsed } from "@/lib/alarmMeta";

// Verbo operacional del evento (encabezado Human-First). Mayúsculas por spec.
// IND-01 (D5) · IDENTIDAD DEL EQUIPO FÍSICO que originó el caso. El snapshot congelado al crear
// el caso manda; para casos históricos sin snapshot se usa el dato actual del catálogo y se marca
// como tal. Nunca se inventan modelo ni ubicación: si faltan, se dice que faltan.
const FAMILIA_LABEL = {
  WS101: "Botón de ayuda WS101", MILESIGHT_WS101: "Botón de ayuda WS101",
  HM: "Hikvision HM", EKT: "Hikvision EKT", EKH: "Hikvision EKH", VS373: "Milesight VS373",
  MOKO: "Botón de ayuda MOKO", MOKO_LW004PB: "Botón de ayuda MOKO",
};

export function origenDispositivo(a) {
  const snap = a?.origen_dispositivo;
  const o = snap || a?.origen_dispositivo_actual;
  if (!o) return null;
  const fam = FAMILIA_LABEL[a.origen_familia] || FAMILIA_LABEL[o.tipo] || o.tipo || "Dispositivo";
  const modelo = o.modelo && !fam.includes(o.modelo) ? o.modelo : (o.modelo ? null : "Sin modelo registrado");
  const donde = o.ubicacion || a.zona_nombre || null;
  return { texto: [fam, modelo, donde].filter(Boolean).join(" · "), congelado: !!snap };
}

export const VERBO_EVENTO = {
  ws101_procesamiento: "ENTREGA WS101 PENDIENTE DE VERIFICACIÓN TÉCNICA",
  caida: "CAÍDA DETECTADA",
  // UI-01C · un MISMO tipo se llamaba de tres formas distintas según la vista ("ALERTA DE
  // FRECUENCIA CARDIACA" en cola/Bitácora/Radar, "FRECUENCIA CARDIACA FUERA DE UMBRAL" en la cola
  // institucional y "LECTURA DE … FUERA DEL UMBRAL" en el detalle). Se conserva UNA redacción
  // factual para todas las superficies. Códigos canónicos, prioridades y familias SIN cambio.
  excepcion_frecuencia_cardiaca: "FRECUENCIA CARDIACA FUERA DE UMBRAL",
  excepcion_frecuencia_respiratoria: "FRECUENCIA RESPIRATORIA FUERA DE UMBRAL",
  boton_panico: "BOTÓN DE AYUDA ACTIVADO",
  excepcion_audio: "AUMENTO SÚBITO DE SONIDO",
  fuera_de_cama_nocturno_sin_retorno: "SALIDA DE CAMA SIN RETORNO",
  fuera_de_cama: "SALIDA DE CAMA",
  retorno_cama: "RETORNO A CAMA",
  salida_bano_sin_retorno: "SALIDA DE BAÑO SIN RETORNO",
  fuera_de_zona_permitida: "FUERA DE ZONA PERMITIDA",
  permanencia_prolongada_zona: "PERMANENCIA PROLONGADA",
  // R8 · catálogo completo del radar VS373. El verbo dice QUÉ REPORTÓ EL SENSOR: ni diagnóstico
  // ni término clínico, y «persona en el piso» NUNCA se presenta como caída.
  inmovilidad_prolongada: "INMOVILIDAD PROLONGADA REPORTADA POR RADAR",
  persona_en_piso_prolongado: "PERSONA EN EL PISO REPORTADA POR RADAR",
  frecuencia_respiratoria_baja: "FRECUENCIA RESPIRATORIA BAJA REPORTADA POR SENSOR",
  frecuencia_respiratoria_alta: "FRECUENCIA RESPIRATORIA ALTA REPORTADA POR SENSOR",
  vs373_ocupacion: "OCUPACIÓN REPORTADA POR RADAR",
  cobertura_perdida: "COBERTURA CRÍTICA INTERRUMPIDA",
  puente_silencioso: "PUENTE SIN SEÑAL",
  dispositivo_silencioso: "SENSOR SIN SEÑAL",
  // MOKO-BAT-01 · el verbo dice lo que REPORTÓ el equipo sobre su propia carga.
  bateria_baja: "BATERÍA BAJA DEL BOTÓN DE AYUDA",
  informativo: "AVISO",
  // v2.13.1 — el origen del evento es la PROGRAMACIÓN, no un sensor.
  recordatorio_programado: "RECORDATORIO PROGRAMADO",
  AUDIO_SESSION_STARTED: "SESIÓN DE AUDIO INICIADA",
  AUDIO_SESSION_ENDED: "SESIÓN DE AUDIO FINALIZADA",
  // NAV-003: hecho operativo registrado FUERA de un caso (no nace de una alarma).
  CONTACTO_OPERATIVO: "CONTACTO OPERATIVO",
};

// Iconografía diferenciada por familia (silueta humana vs infraestructura).
export const ICONO_EVENTO = {
  caida: Activity,
  excepcion_frecuencia_cardiaca: HeartPulse,
  excepcion_frecuencia_respiratoria: Wind,
  boton_panico: Siren,
  excepcion_audio: AudioLines,
  fuera_de_cama_nocturno_sin_retorno: LogOut,
  fuera_de_cama: LogOut,
  salida_bano_sin_retorno: PersonStanding,
  fuera_de_zona_permitida: PersonStanding,
  permanencia_prolongada_zona: Clock,
  inmovilidad_prolongada: Clock,
  persona_en_piso_prolongado: PersonStanding,
  frecuencia_respiratoria_baja: Wind,
  frecuencia_respiratoria_alta: Wind,
  vs373_ocupacion: PersonStanding,
  cobertura_perdida: ShieldOff,
  puente_silencioso: Router,
  dispositivo_silencioso: WifiOff,
  bateria_baja: BatteryWarning,
  sensor_offline: ServerOff,
  AUDIO_SESSION_STARTED: AudioLines,
  AUDIO_SESSION_ENDED: AudioLines,
  CONTACTO_OPERATIVO: PhoneCall,
};

export const verboEvento = (tipo) => VERBO_EVENTO[tipo] || (tipo ? tipo.replace(/_/g, " ").toUpperCase() : "EVENTO");
// INSTITUCIONAL: las lecturas biométricas se nombran como lo que son (dato fuera del umbral
// configurado), sin insinuar diagnóstico. Mismos tipos: sólo etiqueta de presentación.
// UI-01C: la redacción factual ya es la ÚNICA en VERBO_EVENTO, así que no quedan overrides
// institucionales. La función se conserva: es el punto de extensión si alguna vez vuelve a hacer
// falta una etiqueta propia del centro.
const VERBO_EVENTO_INST = {};
export const verboEventoInst = (tipo) => VERBO_EVENTO_INST[tipo] || verboEvento(tipo);
export const iconoEvento = (tipo) => ICONO_EVENTO[tipo] || Bell;
export const esTecnica = (a) => a?.es_tecnica ?? TIPOS_TECNICOS.has(a?.tipo);

// Acento de color de una tarjeta de PERSONA según prioridad (coral ALTA, ámbar MEDIA, cian BAJA).
// Devuelve tokens de color para reutilizar en borde/texto/badge/fondo.
export function acentoPersona(prioridad) {
  const p = prioridad === "maxima" ? "alta" : prioridad;
  // V2.2 · el color semántico se toma de la paleta central (variables CSS): ALTA #FF453A con
  // etiqueta blanca, MEDIA #FF9F0A con etiqueta oscura #1D1D1F y BAJA azul de acción #0A84FF.
  // No se declaran HEX nuevos en los componentes: las clases gv2-* leen las mismas variables.
  if (p === "alta") return { hex: "#FF453A", text: "gv2-alert-text", border: "gv2-border-alert", soft: "gv2-soft", badge: "gv2-badge-alert", ring: "#FF453A" };
  if (p === "media") return { hex: "#FF9F0A", text: "gv2-amber-text", border: "gv2-border-amber", soft: "gv2-soft", badge: "gv2-badge-amber", ring: "#FF9F0A" };
  return { hex: "#0A84FF", text: "gv2-action-text", border: "gv2-border-action", soft: "gv2-soft", badge: "gv2-badge-action", ring: "#0A84FF" };
}

// Franja rayada técnica DISCRETA (no llena la tarjeta): sólo una tira delgada al borde izquierdo.
export const HATCH_TECNICO = {
  backgroundImage:
    "repeating-linear-gradient(45deg, #9B2C2C, #9B2C2C 3px, rgba(155,44,44,0.15) 3px, rgba(155,44,44,0.15) 7px)",
};

// "SIGUIENTE ACCIÓN": guía operativa derivada del estado real del caso (no inventa).
export function siguienteAccion(a) {
  if (!a) return null;
  const tec = esTecnica(a);
  const pasos = (a.action_plan_congelado || {}).pasos || [];
  const pendiente = pasos.find((p) => !p.ejecutado);
  if (a.estado === "recibida") {
    return tec
      ? "Toma la incidencia para iniciar el diagnóstico técnico."
      : "Toma la alarma para iniciar el protocolo de atención.";
  }
  if (a.estado === "cerrada") return "Caso cerrado. Sin acción pendiente.";
  if (pendiente) return pendiente.texto;
  const cui = a.cuidadora_en_turno;
  if (!tec && cui?.nombre) return `Contactar a ${cui.nombre} (cuidadora en turno)${cui.telefono ? ` · ${cui.telefono}` : ""}.`;
  const c0 = (a.contactos_resueltos || [])[0];
  if (!tec && c0?.contacto?.nombre_completo) return `Llamar a ${c0.contacto.nombre_completo} (${c0.relacion_familiar || "contacto"}).`;
  if (a.tipo === "bateria_baja") {
    const niv = a.bateria_panel?.nivel;
    if (niv === "inmediata") return "Carga inmediata del botón. Documenta quién se responsabiliza.";
    if (niv === "recuperacion_observada") return "El equipo reporta carga suficiente. Confírmalo con el sitio antes de cerrar.";
    return "Programa la carga del botón y documenta quién se responsabiliza.";
  }
  if (tec) return "Confirmar luz/internet en el sitio y dar seguimiento hasta el restablecimiento.";
  return "Sigue el protocolo del sitio y documenta la gestión.";
}

// Cronómetro legible: >=24h en "N d HH h"; <24h delega en elapsed (H:MM:SS / MM:SS). No cambia backend.
export function elapsedHuman(fromIso, nowMs) {
  if (!fromIso) return "—";
  const s = Math.max(0, Math.floor((nowMs - new Date(fromIso).getTime()) / 1000));
  if (s >= 86400) {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    return `${d} d ${String(h).padStart(2, "0")} h`;
  }
  return elapsed(fromIso, nowMs);
}

// Nombre corto para la cola: "Rosa Elena Márquez" -> "Rosa E."
export function nombreCorto(nombre) {
  if (!nombre) return "";
  const p = nombre.trim().split(/\s+/);
  if (p.length === 1) return p[0];
  return `${p[0]} ${p[1][0]}.`;
}
